import { useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mostrarToast } from '@/lib/formularioGuiado';
import Botao from '@/components/ui/Botao';
import Chip from '@/components/ui/Chip';
import EstadoVazio from '@/components/ui/EstadoVazio';
import ModalConfirmar from '@/pages/gerenciar/loja/components/ModalConfirmar';
import FolhaInstrucaoPoint from './FolhaInstrucaoPoint';
import { ehPdv, tempoRelativo } from './utils';
import type { InfoMaquininhas, TerminalCaixa } from './types';

interface Props {
  eventoId: number;
  terminais: TerminalCaixa[];
  carregando: boolean;
  info: InfoMaquininhas;
  aoRecarregar: () => void;
  aoAtualizarInfo: (info: Partial<InfoMaquininhas>) => void;
}

interface RespostaTerminais {
  ok?: boolean;
  encontradas_no_mp?: number;
  erro?: string;
  terminais?: unknown[];
  mensagem?: string;
  aviso?: string;
  conta_do_evento?: boolean;
  da_conta_do_evento?: number;
}

interface RespostaAcao {
  ok?: boolean;
  erro?: string;
  mensagem?: string;
  impressa?: boolean;
}

const REDE = 'Sem conexão agora. Tente de novo.';

// Apelido que o banco dá às maquininhas da conta Mercado Pago do evento.
const SUFIXO_CONTA_DO_EVENTO = '· conta do evento';

function ehDaContaDoEvento(t: TerminalCaixa): boolean {
  return t.apelido.trim().toLowerCase().endsWith(SUFIXO_CONTA_DO_EVENTO);
}

export default function ListaMaquininhas({
  eventoId,
  terminais,
  carregando,
  info,
  aoRecarregar,
  aoAtualizarInfo,
}: Props) {
  const [buscando, setBuscando] = useState(false);
  const [vazioMp, setVazioMp] = useState(false);
  const [ligando, setLigando] = useState<string | null>(null);
  const [testando, setTestando] = useState<string | null>(null);
  const [prontos, setProntos] = useState<Record<string, boolean>>({});
  const [menu, setMenu] = useState<string | null>(null);
  const [instrucao, setInstrucao] = useState<TerminalCaixa | null>(null);
  const [confirmarNormal, setConfirmarNormal] = useState<TerminalCaixa | null>(null);

  // Conversa com o servidor guardando o que veio de recado.
  function guardarInfo(r: RespostaTerminais) {
    aoAtualizarInfo({
      contaDoEvento: r.conta_do_evento === true,
      daContaDoEvento: Number(r.da_conta_do_evento) || 0,
      mensagem: r.mensagem?.trim() || null,
      aviso: r.aviso?.trim() || null,
    });
  }

  async function procurar() {
    setBuscando(true);
    try {
      const { data, error } = await supabase.functions.invoke('caixa-cobrar', {
        body: { acao: 'terminais', evento_id: eventoId },
      });
      if (error) {
        mostrarToast(REDE, 5000, 'erro');
        return;
      }
      const r = (data ?? {}) as RespostaTerminais;
      guardarInfo(r);
      if (!r.ok) {
        mostrarToast(r.erro || 'Não foi possível procurar agora.', 5000, 'erro');
        return;
      }
      aoRecarregar();
      const n = r.encontradas_no_mp ?? 0;
      setVazioMp(n === 0);
      if (n === 0) mostrarToast('Nenhuma maquininha encontrada.', 5000, 'aviso');
      else mostrarToast(`${n} maquininha(s) encontrada(s)`, 4000, 'sucesso');
    } catch {
      mostrarToast(REDE, 5000, 'erro');
    } finally {
      setBuscando(false);
    }
  }

  async function ligar(t: TerminalCaixa) {
    setLigando(t.terminal_externo);
    try {
      const { data, error } = await supabase.functions.invoke('caixa-cobrar', {
        body: { acao: 'modo_pdv', evento_id: eventoId, terminal_externo: t.terminal_externo },
      });
      if (error) {
        mostrarToast(REDE, 5000, 'erro');
        return;
      }
      const r = (data ?? {}) as RespostaAcao;
      if (!r.ok) {
        mostrarToast(r.erro || 'Não foi possível ligar agora.', 5000, 'erro');
        return;
      }
      aoRecarregar();
      setInstrucao(t);
    } catch {
      mostrarToast(REDE, 5000, 'erro');
    } finally {
      setLigando(null);
    }
  }

  async function testar(t: TerminalCaixa) {
    setTestando(t.terminal_externo);
    try {
      const { data, error } = await supabase.functions.invoke('caixa-cobrar', {
        body: { acao: 'testar', evento_id: eventoId, terminal_externo: t.terminal_externo },
      });
      if (error) {
        mostrarToast(REDE, 6000, 'erro');
        return;
      }
      const r = (data ?? {}) as RespostaAcao;
      if (!r.ok) {
        mostrarToast(r.erro || 'Não foi possível testar agora.', 6000, 'erro');
        return;
      }
      if (r.impressa === true) {
        mostrarToast('Saiu o papel. Maquininha pronta!', 4000, 'sucesso');
        setProntos((p) => ({ ...p, [t.terminal_externo]: true }));
        window.setTimeout(
          () => setProntos((p) => ({ ...p, [t.terminal_externo]: false })),
          60_000,
        );
        aoRecarregar();
      } else {
        mostrarToast(
          r.mensagem || 'A maquininha não imprimiu. Deixe-a em “Vendas vinculadas”.',
          7000,
          'aviso',
        );
      }
    } catch {
      mostrarToast(REDE, 6000, 'erro');
    } finally {
      setTestando(null);
    }
  }

  async function modoNormal(t: TerminalCaixa) {
    setConfirmarNormal(null);
    try {
      const { data, error } = await supabase.functions.invoke('caixa-cobrar', {
        body: { acao: 'modo_normal', evento_id: eventoId, terminal_externo: t.terminal_externo },
      });
      if (error) {
        mostrarToast(REDE, 5000, 'erro');
        return;
      }
      const r = (data ?? {}) as RespostaAcao;
      if (!r.ok) {
        mostrarToast(r.erro || 'Não foi possível agora.', 5000, 'erro');
        return;
      }
      mostrarToast(r.mensagem || 'A maquininha voltou ao modo normal.', 4000, 'sucesso');
      aoRecarregar();
    } catch {
      mostrarToast(REDE, 5000, 'erro');
    }
  }

  function linha(t: TerminalCaixa) {
    const pdv = ehPdv(t);
    const visto = tempoRelativo(t.visto_em);
    const testandoEste = testando === t.terminal_externo;
    return (
      <li
        key={t.terminal_externo}
        className="animate-subir rounded-2xl border border-secondary-100 bg-background-50 p-4"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="truncate text-[16px] font-bold text-foreground-950">{t.apelido}</p>
            {t.modelo && (
              <p className="mt-0.5 truncate text-[13px] text-foreground-500">{t.modelo}</p>
            )}
          </div>
          <div className="relative shrink-0">
            <button
              type="button"
              onClick={() => setMenu((m) => (m === t.terminal_externo ? null : t.terminal_externo))}
              aria-label="Mais opções"
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-xl text-foreground-400 transition-colors hover:bg-background-100 hover:text-foreground-700"
            >
              <i className="ri-more-2-fill text-xl" />
            </button>
            {menu === t.terminal_externo && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setMenu(null)} aria-hidden />
                <div className="absolute right-0 z-20 mt-1 w-52 overflow-hidden rounded-2xl border border-secondary-100 bg-white py-1 shadow-[0_10px_30px_rgba(20,25,60,.16)]">
                  <button
                    type="button"
                    onClick={() => {
                      setMenu(null);
                      setConfirmarNormal(t);
                    }}
                    className="flex w-full cursor-pointer items-center gap-2 px-4 py-3 text-left text-[14px] font-medium text-foreground-800 transition-colors hover:bg-background-100"
                  >
                    <span className="flex h-5 w-5 items-center justify-center">
                      <i className="ri-restart-line" />
                    </span>
                    Voltar ao modo normal
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-2">
          {prontos[t.terminal_externo] ? (
            <Chip tipo="success">Pronta ✓</Chip>
          ) : pdv ? (
            <Chip tipo="success">Ligada ao sistema</Chip>
          ) : (
            <Chip tipo="neutro">Modo normal</Chip>
          )}
          {ehDaContaDoEvento(t) && <Chip tipo="info">Conta do evento</Chip>}
          {!t.imprime && <Chip tipo="warning">Sem impressora</Chip>}
        </div>

        {t.em_uso_por && (
          <p className="mt-2 text-[13px] text-foreground-500">Em uso em: {t.em_uso_por}</p>
        )}
        {visto && <p className="mt-1 text-[13px] text-foreground-400">{visto}</p>}

        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
          {!pdv && (
            <Botao
              variante="primario"
              tamanho="lg"
              cheio
              icone="ri-plug-2-line"
              carregando={ligando === t.terminal_externo}
              onClick={() => ligar(t)}
            >
              Ligar ao sistema
            </Botao>
          )}
          <Botao
            variante={pdv ? 'primario' : 'secundario'}
            tamanho="lg"
            cheio
            icone="ri-printer-line"
            carregando={testandoEste}
            onClick={() => testar(t)}
          >
            {testandoEste ? 'Imprimindo…' : 'Testar impressão'}
          </Botao>
        </div>
        {testandoEste && (
          <p className="mt-2 text-center text-[14px] text-foreground-500">Olhe a maquininha.</p>
        )}
      </li>
    );
  }

  const daConta = terminais.filter(ehDaContaDoEvento);
  const doSeliga = terminais.filter((t) => !ehDaContaDoEvento(t));
  // Só mostra os grupos quando existe maquininha da conta do evento.
  const temGrupos = daConta.length > 0;

  return (
    <div className="rounded-3xl bg-white p-4 shadow-[0_6px_18px_rgba(20,25,60,.06)]">
      <div className="flex items-center gap-2 px-1">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
          <i className="ri-device-line" />
        </span>
        <h3 className="text-[17px] font-bold text-foreground-950">Maquininhas</h3>
      </div>
      <p className="mt-1 px-1 text-[14px] text-foreground-500">
        Ligue a maquininha da Point ao sistema em três toques.
      </p>

      <div className="mt-3">
        <Botao
          variante="secundario"
          tamanho="md"
          cheio
          icone="ri-search-line"
          carregando={buscando}
          onClick={procurar}
        >
          Procurar maquininhas
        </Botao>
      </div>

      {info.mensagem && (
        <p className="mt-3 flex items-start gap-2 rounded-xl border border-background-200 bg-background-100 px-3 py-2 text-[13px] leading-relaxed text-foreground-700">
          <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
            <i className="ri-information-line" aria-hidden />
          </span>
          {info.mensagem}
        </p>
      )}
      {info.aviso && (
        <p className="mt-2 flex items-start gap-2 rounded-xl border border-warning-300 bg-warning-100 px-3 py-2 text-[13px] leading-relaxed text-warning-900">
          <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
            <i className="ri-alert-line" aria-hidden />
          </span>
          {info.aviso}
        </p>
      )}

      {carregando ? (
        <div className="mt-4 space-y-3">
          {[0, 1].map((i) => (
            <div key={i} className="h-28 animate-pulse rounded-2xl bg-secondary-100" />
          ))}
        </div>
      ) : terminais.length === 0 ? (
        <div className="mt-4">
          {vazioMp ? (
            <EstadoVazio
              icone="ri-device-line"
              titulo="Nenhuma maquininha nesta conta do Mercado Pago"
              descricao="Ligue a maquininha, entre nela com a conta do Mercado Pago e toque em Procurar de novo."
            />
          ) : (
            <EstadoVazio
              icone="ri-device-line"
              titulo="Nenhuma maquininha ligada ainda"
              descricao="Ligue a sua Point, deixe na mesma conta do Mercado Pago e toque em Procurar maquininhas."
              acaoRotulo="Procurar maquininhas"
              aoClicarAcao={procurar}
            />
          )}
        </div>
      ) : (
        <>
          {temGrupos ? (
            <div className="mt-4 space-y-4">
              <div>
                <p className="mb-2 px-1 text-[13px] font-bold text-foreground-950">
                  Maquininhas da conta do evento
                </p>
                <ul className="space-y-3">{daConta.map(linha)}</ul>
              </div>
              {doSeliga.length > 0 && (
                <div>
                  <p className="mb-2 px-1 text-[13px] font-bold text-foreground-950">
                    Maquininhas do Se Liga
                  </p>
                  <ul className="space-y-3">{doSeliga.map(linha)}</ul>
                </div>
              )}
            </div>
          ) : (
            <ul className="mt-4 space-y-3">{terminais.map(linha)}</ul>
          )}

          <p className="mt-4 px-1 text-[13px] leading-relaxed text-foreground-500">
            O dinheiro do cartão cai na conta dona da maquininha. Para cair direto na conta do
            evento, cadastre a maquininha na conta Mercado Pago conectada em Mais › O dinheiro.
          </p>
        </>
      )}

      <FolhaInstrucaoPoint
        aberto={instrucao !== null}
        apelido={instrucao?.apelido ?? ''}
        testando={instrucao != null && testando === instrucao.terminal_externo}
        aoFechar={() => setInstrucao(null)}
        aoTestar={() => {
          if (instrucao) void testar(instrucao);
        }}
      />

      {confirmarNormal && (
        <ModalConfirmar
          titulo="Voltar ao modo normal"
          mensagem="A maquininha deixa de receber cobranças do Se Liga e volta a vender sozinha. Continuar?"
          rotuloConfirmar="Continuar"
          aoConfirmar={() => modoNormal(confirmarNormal)}
          aoCancelar={() => setConfirmarNormal(null)}
        />
      )}
    </div>
  );
}